import { Button, Form, SelectBox } from 'devextreme-react';
import {
  ButtonItem,
  ButtonOptions,
  GroupItem,
  Label,
  RequiredRule,
  SimpleItem,
} from 'devextreme-react/form';
import notify from 'devextreme/ui/notify';
import React, { useCallback, useEffect, useState } from 'react';
import useSWR from 'swr';
import { putApi } from '../../apiUtils';
import { useTranslation } from '../../contexts/translation';
import { swrConfig } from '../../pages/Assets/AssetsEdit';
import { Person } from '../../types/person.types';
import { getUserColorClass, UserColor } from '../../types/userColor.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import './InformWatchersForm.styles.scss';

interface Props {
  taskId: number;
  inputCss: string;
  buttonCss: string;
  onSuccess: () => void;
}

export const InformWatchersForm = ({ taskId, inputCss, buttonCss, onSuccess }: Props) => {
  const { translate } = useTranslation();
  const transFormName = 'Task.UpdateServiceDesk';

  const [comment, setComment] = useState('');
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [localWatchers, setLocalWatchers] = useState<Person[]>([]);

  const { data: responsiblePersons } = useSWR<Person[]>(API_URL.PERSON_FROM_THIS_APP_LIST, fetcher);
  const { data: watchers } = useSWR<Person[]>(
    `${API_URL.TASK_SPARE_WATCHERS_LIST}?id=${taskId}`,
    fetcher,
  );

  const [userColorClass, setUserColorClass] = useState<string | null>(null);
  const { data: userColors } = useSWR<UserColor[]>(
    `${API_URL.USER_COLOR_LIST}`,
    fetcher,
    swrConfig,
  );

  const addWatcher = useCallback(() => {
    if (!responsiblePersons) return;

    const isInLocalWatchers =
      localWatchers.filter((x) => x.personalNumber === selectedPersonId).length > 0;
    if (isInLocalWatchers) return;

    const person = responsiblePersons.filter((x) => x.personalNumber === selectedPersonId);
    if (!person || person.length === 0) return;

    const _watchers = [...localWatchers];
    _watchers.push(person[0]);
    setLocalWatchers(_watchers);
    setSelectedPersonId(null);
  }, [localWatchers, selectedPersonId, responsiblePersons]);

  const removeWatcher = useCallback(
    (id: number) => {
      const _watchers = localWatchers.filter((x) => x.personalNumber !== id);
      setLocalWatchers(_watchers);
    },
    [localWatchers],
  );

  const submitInformWatchers = useCallback(async () => {
    if (!localWatchers || localWatchers.length === 0) {
      notify(translate!('Select at least one watcher', transFormName), 'error');
      return;
    }

    await putApi({
      url: API_URL.TASK_INFORM_WATCHERS,
      data: {
        taskId,
        watcherIds: localWatchers.map((x) => x.personalNumber),
        comment: comment,
      },
      callAfterSuccess: async (data) => {
        onSuccess();
      },
    });
  }, [localWatchers, comment]);

  useEffect(() => {
    if (!watchers) return;
    setLocalWatchers(watchers);
  }, [watchers]);

  return (
    <>
      <div className="watchers-container">
        <SelectBox
          className={`dropdown-user-color ${userColorClass}`}
          dataSource={responsiblePersons}
          valueExpr="personalNumber"
          displayExpr="fullName"
          value={selectedPersonId}
          onValueChanged={(e) => {
            setSelectedPersonId(e.value);
            if (!userColors || userColors.length === 0) return;

            const colorClass = getUserColorClass(userColors, e.value);
            setUserColorClass(colorClass);
          }}
          placeholder={translate!('Select watcher', transFormName)}
          searchEnabled={true}
          buttons={[
            {
              name: 'customButton',
              location: 'after',
              options: {
                icon: 'add',
                stylingMode: 'text',
                type: 'default',
                onClick: () => {
                  addWatcher();
                },
              },
            },
          ]}
        />
        <div className="list">
          {localWatchers.map((watcher) => {
            return (
              <div key={watcher.personalNumber} className="watcher">
                <div
                  className={`name dropdown-user-color ${
                    userColors ? getUserColorClass(userColors, watcher.personalNumber) : ''
                  }`}
                >
                  <span className="text">
                    {watcher.fullName} {watcher.email ? `(${watcher.email})` : ''}
                  </span>
                </div>
                <Button
                  icon="remove"
                  className="remove"
                  stylingMode="text"
                  type="default"
                  onClick={() => removeWatcher(watcher.personalNumber)}
                />
              </div>
            );
          })}
        </div>
      </div>
      <form>
        <Form labelLocation={'top'} formData={{ comment }}>
          <GroupItem>
            <SimpleItem
              dataField="comment"
              editorType="dxTextArea"
              editorOptions={{
                value: comment,
                onValueChanged: (e: any) => setComment(e.value),
              }}
              cssClass={inputCss}
            >
              <RequiredRule message={translate!('Comment is required', transFormName)} />
              <Label text={translate!('Comment', transFormName)} />
            </SimpleItem>
          </GroupItem>
          <ButtonItem
            verticalAlignment={'center'}
            horizontalAlignment={'left'}
            cssClass={buttonCss}
          >
            <ButtonOptions
              text={translate!('Send', transFormName)}
              type={'success'}
              useSubmitBehavior={false}
              onClick={() => submitInformWatchers()}
            />
          </ButtonItem>
        </Form>
      </form>
    </>
  );
};
